import { useState, useEffect, useCallback } from 'react';
import { artikelService } from '../services/ArtikelService';
import useAlert from '../../../shared/hooks/useAlert';
import { usePagination } from '../../../shared/hooks/usePagination';

export const useArtikel = () => {
  const { alert, showAlert } = useAlert();

  // Gunakan hook pagination dari utils
  const {
    // State dari usePagination
    pagination,
    data,
    meta,
    links,
    loading,
    error,

    // Setters dari usePagination
    setData,
    setLoading,
    setError,
    setMeta,
    setLinks,

    // Actions dari usePagination
    updateFromResponse,
    updatePagination,
    goToPage,
    changeLimit,
    changeSort,
    toggleSortOrder,
    search,
    resetPagination,

    // Utilities dari usePagination
    buildQueryParams,
    getQueryObject: getBaseQueryObject,
    getSortInfo,

    // Computed values dari usePagination
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    currentSortField,
    currentSortOrder,
  } = usePagination();

  // State untuk filters
  const [filters, setFilters] = useState({
    status: 'ALL',
    type: 'ALL',
    typeKonten: 'ALL',
  });

  // Set artikel dari data
  const artikel = data;

  // Build query object dengan filters
  const getQueryObject = useCallback(() => {
    const baseQuery = getBaseQueryObject();

    // Add filter parameters if not 'ALL'
    const queryWithFilters = {
      ...baseQuery,
      ...(filters.status !== 'ALL' && { status: filters.status }),
      ...(filters.type !== 'ALL' && { type_artikel: filters.type }),
      ...(filters.typeKonten !== 'ALL' && { type_konten: filters.typeKonten }),
    };

    return queryWithFilters;
  }, [getBaseQueryObject, filters]);

  // Build query params untuk API call
  const buildQueryParamsWithFilters = useCallback(() => {
    const queryObject = getQueryObject();
    const params = new URLSearchParams();

    Object.entries(queryObject).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    return params.toString();
  }, [getQueryObject]);

  // Fetch artikel dengan pagination dan filters
  const fetchArtikel = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryObject = getQueryObject();
      const queryParams = buildQueryParamsWithFilters();

      const result = await artikelService.getPaginate(queryParams);

      if (result.success) {
        updateFromResponse(result.data);
      } else {
        console.error('❌ API Error:', result.error);
        setError(result.error);
        showAlert(result.error, 'danger');
      }
    } catch (err) {
      console.error('🚨 Network Error:', err);
      const errorMessage = 'Network error occurred';
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
    } finally {
      setLoading(false);
    }
  }, [buildQueryParamsWithFilters, setError, setLoading, showAlert, updateFromResponse, getQueryObject, filters]);

  // Update filter functions
  const updateStatusFilter = useCallback((status) => {
    setFilters(prev => ({ ...prev, status }));
    goToPage(1); // Reset to page 1 when filter changes
  }, [goToPage]);

  const updateTypeFilter = useCallback((type) => {
    setFilters(prev => ({ ...prev, type }));
    goToPage(1); // Reset to page 1 when filter changes
  }, [goToPage]);

  const updateTypeKontenChange = useCallback((typeKonten) => {
    setFilters(prev => ({ ...prev, typeKonten }));
    goToPage(1); // Reset to page 1 when filter changes
  }, [goToPage]);

  const clearFilters = useCallback(() => {
    setFilters({
      status: 'ALL',
      type: 'ALL',
      typeKonten: 'ALL',
    });
    goToPage(1);
  }, [goToPage]);

  // Artikel CRUD operations
  const addArtikel = useCallback(async (artikelData) => {
    setLoading(true);
    try {
      const result = await artikelService.create(artikelData);
      if (result.success) {
        showAlert('Artikel created successfully!', 'success');
        fetchArtikel(); // Refresh data
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        showAlert(result.error, 'danger');
        return {
          success: false,
          error: result.error,
          validationErrors: result.validationErrors
        };
      }
    } catch (err) {
      const errorMessage = 'Network error occurred';
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [fetchArtikel, setError, setLoading, showAlert]);

  const fetchArtikelById = useCallback(async (id) => {
    try {
      const result = await artikelService.getById(id);
      if (result.success) {
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        showAlert(result.error, 'danger');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = 'Network error occurred';
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
      return { success: false, error: errorMessage };
    }
  }, [setError, showAlert]);

  const editArtikel = useCallback(async (id, updatedArtikel) => {
    setLoading(true);
    try {
      const result = await artikelService.update(id, updatedArtikel);
      if (result.success) {
        // Update item di state
        setData(prevData =>
          prevData.map(p => (p.id === id ? result.data : p))
        );
        showAlert('Artikel updated successfully!', 'success');
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        showAlert(result.error, 'danger');
        return {
          success: false,
          error: result.error,
          validationErrors: result.validationErrors
        };
      }
    } catch (err) {
      const errorMessage = 'Network error occurred';
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [setData, setError, setLoading, showAlert]);

  const removeArtikel = useCallback(async (id) => {
    try {
      const result = await artikelService.delete(id);
      if (result.success) {
        // Remove item dari state
        setData(prevData => prevData.filter(p => p.id !== id));
        showAlert('Artikel deleted successfully!', 'success');
        return { success: true };
      } else {
        setError(result.error);
        showAlert(result.error, 'danger');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = 'Network error occurred';
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
      return { success: false, error: errorMessage };
    }
  }, [setData, setError, showAlert]);

  const updateOnActive = useCallback(async (id, updatedStatus) => {
    try {
      const result = await artikelService.updateActive(id, updatedStatus);
      if (result.success) {
        // Update item di state
        setData(prevData =>
          prevData.map(p => (p.id === id ? result.data : p))
        );
        showAlert('Status updated successfully!', 'success');
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        showAlert(result.error, 'danger');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = 'Network error occurred';
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
      return { success: false, error: errorMessage };
    }
  }, [setData, setError, showAlert]);

  // Fetch data saat pagination atau filters berubah
  useEffect(() => {
    fetchArtikel();
  }, [
    pagination.page,
    pagination.limit,
    pagination.sortBy,
    pagination.search,
    filters.status,
    filters.type,
    filters.typeKonten,
  ]);

  return {
    // Data state
    artikel,
    loading,
    error,
    alert,

    // Pagination state
    pagination,
    meta,
    links,

    // Filter state
    filters,

    // Pagination actions
    fetchArtikel,
    goToPage,
    changeLimit,
    changeSort,
    toggleSortOrder,
    search,
    resetPagination,

    // Filter actions
    updateStatusFilter,
    updateTypeFilter,
      updateTypeKontenChange,
    clearFilters,

    // Pagination info
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    currentSortField,
    currentSortOrder,

    // Artikel CRUD operations
    addArtikel,
    fetchArtikelById,
    editArtikel,
    removeArtikel,
    updateOnActive,

    // Utilities
    buildQueryParams: buildQueryParamsWithFilters,
    getQueryObject,
    getSortInfo,
  };
};